import * as React from "react";
import { cn } from "@/lib/utils";

interface CurrencyInputProps {
  value: string | number | null | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  id?: string;
}

function formatToCurrency(cents: number): string {
  if (cents === 0) return "";
  const reais = (cents / 100).toFixed(2);
  const [intPart, decPart] = reais.split(".");
  const formatted = intPart!.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `R$ ${formatted},${decPart}`;
}

function parseToCents(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Math.round(value * 100);
  const str = String(value).trim();
  const hasComma = str.includes(",");
  let num: number;
  if (hasComma) {
    num = parseFloat(str.replace(/[^\d,]/g, "").replace(/\./g, "").replace(",", "."));
  } else {
    num = parseFloat(str.replace(/[^\d.]/g, ""));
  }
  if (isNaN(num)) return 0;
  return Math.round(num * 100);
}

const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ value, onChange, placeholder = "R$ 0,00", className, required, id }, ref) => {
    const [displayValue, setDisplayValue] = React.useState("");
    const internalChange = React.useRef(false);

    React.useEffect(() => {
      if (internalChange.current) {
        internalChange.current = false;
        return;
      }
      const cents = parseToCents(value);
      setDisplayValue(cents > 0 ? formatToCurrency(cents) : "");
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/\D/g, "");
      if (!raw) {
        internalChange.current = true;
        setDisplayValue("");
        onChange("");
        return;
      }
      const cents = parseInt(raw, 10);
      const decimalValue = (cents / 100).toFixed(2);
      internalChange.current = true;
      setDisplayValue(formatToCurrency(cents));
      onChange(decimalValue);
    };

    return (
      <input
        ref={ref}
        id={id}
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
      />
    );
  }
);

CurrencyInput.displayName = "CurrencyInput";

export { CurrencyInput };
